import { Button, Modal, Select, Checkbox } from 'antd';
import { css } from 'emotion';
import moment, { Moment } from 'moment';
import { useGetMentionableUsers } from 'waypoint-hooks';
import { Report, UpdatedEntityReport } from 'waypoint-types';
import { DatePicker } from 'waypoint-react';

interface ReportModalProps {
    onSave: () => void;
    onCancel: () => void;
    onDueDateChange: (date: moment.Moment | null) => void;
    onAssigneeChange: (value: string[]) => void;
    onReviewerChange: (value: string[]) => void;
    onSendAssigneeNotificationChange: (value: boolean) => void;
    onSendReviewerNotificationChange: (value: boolean) => void;
    batchValues: UpdatedEntityReport;
    selectedEntityCodes: string[];
    selectedReport: Report | undefined;
}

const showCheckBox = (visible: boolean) => css`
    height: ${visible ? '32px' : '0px'} !important;
    opacity: ${visible ? '1' : '0'} !important;
    transition: 300ms all ease-out !important;
    margin-top: ${visible ? '6px' : '0px'} !important;
`;

const wrapper = css`
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
`;

const title = css`
    font-weight: bold;
    margin-right: 16px;
`;

export const ReportModal = ({
    onSave,
    onCancel,
    onDueDateChange,
    onAssigneeChange,
    onReviewerChange,
    onSendAssigneeNotificationChange,
    onSendReviewerNotificationChange,
    batchValues,
    selectedEntityCodes,
    selectedReport,
}: ReportModalProps) => {
    const userWorkflowRoleOptions =
        useGetMentionableUsers(selectedEntityCodes) ?? [];

    const disabledDate = (current: Moment | null): boolean => {
        if (!selectedReport?.due_date) {
            return false;
        }

        return !!(
            current && current > moment(selectedReport?.due_date).endOf('day')
        );
    };

    return (
        <Modal
            title="Edit properties"
            open={true}
            onOk={onSave}
            closeIcon={<></>}
            footer={[
                <Button key="back" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={onSave}>
                    Save
                </Button>,
            ]}
            data-testid="report-edition-modal"
        >
            <div className={wrapper}>
                <span className={title}>Due Date</span>
                <DatePicker
                    value={batchValues.dueDate}
                    style={{ width: '100%', marginTop: 6 }}
                    onChange={onDueDateChange}
                    disabledDate={disabledDate}
                    data-test="report-due-date-picker"
                />
            </div>

            <div className={wrapper}>
                <span className={title}>Assignee</span>
                <Select
                    allowClear
                    mode="multiple"
                    placeholder="Select assignee"
                    style={{ width: '100%', marginTop: 6 }}
                    onChange={onAssigneeChange}
                    value={batchValues.assignee}
                    filterOption={(input, option) => {
                        const optionToFilter = `${option?.label}` ?? '';
                        return optionToFilter
                            .toLowerCase()
                            .includes(input.toLowerCase());
                    }}
                    options={userWorkflowRoleOptions.map((user: any) => ({
                        label: user.text,
                        value: user.id,
                    }))}
                    data-test="report-assignee-select"
                />

                <Checkbox
                    className={showCheckBox(batchValues.assignee.length > 0)}
                    onChange={(e) =>
                        onSendAssigneeNotificationChange(e.target.checked)
                    }
                    data-test="report-assignee-checkbox"
                >
                    Notify Assignees
                </Checkbox>
            </div>

            <div className={wrapper}>
                <span className={title}>Reviewer</span>
                <Select
                    allowClear
                    mode="multiple"
                    placeholder="Select reviewer"
                    style={{ width: '100%', marginTop: 6 }}
                    value={batchValues.reviewer}
                    onChange={onReviewerChange}
                    filterOption={(input, option) => {
                        const optionToFilter = `${option?.label}` ?? '';
                        return optionToFilter
                            .toLowerCase()
                            .includes(input.toLowerCase());
                    }}
                    options={userWorkflowRoleOptions.map((user: any) => ({
                        label: user.text,
                        value: user.id,
                    }))}
                    data-test="report-reviewer-select"
                />

                <Checkbox
                    className={showCheckBox(batchValues.reviewer.length > 0)}
                    onChange={(e) =>
                        onSendReviewerNotificationChange(e.target.checked)
                    }
                    data-test="report-reviewer-checkbox"
                >
                    Notify Reviewers
                </Checkbox>
            </div>
        </Modal>
    );
};
